import {
  Profiler,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { formatBytes } from "@suite/core";
import { AdBanner, useToast } from "@suite/ui";
import { Explainer, Footer } from "./Explainer.tsx";
import {
  addSource,
  applyPreset,
  aspectLocked,
  boxHeight,
  clampTransform,
  clipDuration,
  clipEnd,
  emptyProject,
  projectDuration,
  removeClip,
  serializeProject,
  sourceById,
  reorderClip,
  setTransform,
  canReorder,
  boxMatchesSource,
  clipsInTrack,
  cycleFit,
  fitMatters,
  matchingPreset,
  removeTrack,
  resetAspect,
  addCleanCopy,
  cleanCopyOf,
  removeCleanCopy,
  rotateClip,
  setAspectLocked,
  setSizeMode,
  sizeMode,
  soundFor,
  setTrackMuted,
  setTrackVolume,
  splitClip,
  topTrack,
  trimClip,
  moveClip,
  updateClip,
  addTextClip,
  newId,
  clipBoxHeight,
  clipLabel,
  fitTextBox,
  isVisual,
  type Clip,
  type ClipKind,
  type PresetName,
  type Project,
  type SourceRef,
} from "./timeline.ts";
import { Stage } from "./Stage.tsx";
import { Group, IconButton, MeasureField, Segmented, Slider } from "./controls.tsx";
import { TextPanel } from "./TextPanel.tsx";
import { AnimationPanel, CompositionPanel } from "./CompositionPanel.tsx";
import { ACCEPT, handleFromDrop, probeFile, type ResolvedSource, matchWarning, canPersistHandles } from "./sources.ts";
import { renderFrame } from "./render.ts";
import {
  Clock,
  MediaPool,
  canKeepTime,
  pickMaster,
  syncElements,
  timeFromElement,
} from "./playback.ts";
import { Diagnostics } from "./Diagnostics.ts";
import { PlayheadStore, usePlayheadDerived } from "./playhead.ts";
import { OpenDialog, OtterSays, type Staged } from "./OpenDialog.tsx";
import { CameraPeek, RecordStrip, useRecorder } from "./Recorder.tsx";
import { canRecord } from "./record.ts";
import { pickMedia, reopen, supply, assign, type Picked, type Supplied } from "./relink.ts";
import { deleteProject, listProjects, loadProject, saveProject, storageAvailable, type ProjectSummary } from "./store.ts";
import {
  defaultSettings,
  estimatedAudioBytes,
  estimatedBytes,
  frameRateChoices,
  isAudioOnly,
  outputSize,
  resolutionChoices,
  videoBitrate,
  type ExportSettings,
  type Quality,
} from "./exportSettings.ts";
import {
  UNKNOWN_CAPABILITIES,
  exportBlockedReason,
  probeCapabilities,
  type Capabilities,
} from "./capabilities.ts";

/** Stage 1 keeps nothing between visits; projects arrive with the store. */
export const STORAGE_KEY = "otterstudio:none";

/** Pixels per second at zoom 1. The timeline is the one place a fixed scale helps. */
const BASE_PPS = 64;

/**
 * How close an element must get to the held playhead before it is trusted to
 * keep time. The clock holds still while it leads, so anything within half a
 * second has plainly completed its seek rather than still being on its way
 * from wherever it was before.
 */
const HANDOVER_TOLERANCE = 0.5;

/**
 * How long the clock may hold before giving up and following whatever is
 * there. A seek that has not completed in this long is not going to, and a
 * frozen playhead is worse than an approximate one.
 */
const MAX_HOLD_MS = 2500;

export function App() {
  const { toast } = useToast();
  const [project, setProject] = useState<Project>(() => emptyProject());
  const [selected, setSelected] = useState<string | null>(null);
  /** The title made a moment ago, so the inspector can put the caret in its words. */
  const [freshText, setFreshText] = useState<string | null>(null);
  // The playhead is not React state, and not App's business past writing it.
  // See PlayheadStore for why.
  const playhead = useRef(new PlayheadStore()).current;
  const [playing, setPlaying] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [caps, setCaps] = useState<Capabilities>(UNKNOWN_CAPABILITIES);
  /**
   * The export dialog: choosing settings, then running. Either way the editor
   * underneath is inert, because the export drives the same media elements the
   * preview does and an edit would seek them out from under the encoder.
   */
  const [exporting, setExporting] = useState<ExportState>(null);
  const [exportSettings, setExportSettings] = useState<ExportSettings | null>(null);
  /** The clip whose noise is being removed right now, and how far along. */
  const [denoising, setDenoising] = useState<Denoising>(null);
  /** The projects dialog, when open, with what it should show. */
  const [dialog, setDialog] = useState<{ projects: ProjectSummary[]; start?: Staged } | null>(null);
  /**
   * Bumped when the set of resolved files changes without the project doing
   * so. The files live in a ref, so nothing else would tell React to look.
   */
  const [, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision((n) => n + 1), []);
  /** Stored handles for sources whose files have not been supplied yet. */
  const pendingHandles = useRef(new Map<string, FileSystemFileHandle>());
  /**
   * A source whose first clip should be selected as soon as it is on the
   * timeline. Files arrive through a functional project update, which React
   * may run more than once in development and which must therefore have no
   * side effects: selecting from inside it could pick the clip id of a run
   * that was thrown away. So the update says nothing, and this effect selects.
   */
  const selectSource = useRef<string | null>(null);
  useEffect(() => {
    const sourceId = selectSource.current;
    if (!sourceId) return;
    const clip = project.clips.find((entry) => entry.sourceId === sourceId);
    if (!clip) return;
    selectSource.current = null;
    setSelected(clip.id);
  }, [project]);
  /** Projects this session has written, so an emptied one keeps being written. */
  const saved = useRef(new Set<string>());
  const storageWarned = useRef(false);
  const [loading, setLoading] = useState(false);

  // Files are held outside React state on purpose: they are large, they are not
  // serialisable, and nothing about them belongs in the project record.
  const sources = useRef(new Map<string, ResolvedSource>());
  const clock = useRef(new Clock());
  const pool = useRef<MediaPool | null>(null);
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const cancel = useRef<AbortController | null>(null);

  if (!pool.current) pool.current = new MediaPool(sources.current);

  // `?debug` in the URL puts a live readout in the corner. See Diagnostics.ts.
  const diagnostics = useRef<Diagnostics | null>(null);
  useEffect(() => {
    if (!Diagnostics.enabled()) return;
    diagnostics.current = new Diagnostics();
    return () => {
      diagnostics.current?.destroy();
      diagnostics.current = null;
    };
  }, []);
  useEffect(() => {
    void probeCapabilities().then(setCaps);
  }, []);

  // Something to reopen? Then offer to. Nothing saved means no dialog at all.
  useEffect(() => {
    if (!storageAvailable()) return;
    void listProjects()
      .then((projects) => {
        if (projects.length > 0) setDialog({ projects });
      })
      .catch(() => undefined);
  }, []);

  /**
   * Autosave. Every edit rewrites the record, half a second after the last
   * one. A project with nothing on it is not written until it has something,
   * so a visit that adds nothing leaves nothing behind.
   */
  useEffect(() => {
    if (!storageAvailable()) return;
    if (project.clips.length === 0 && !saved.current.has(project.id)) return;
    const timer = setTimeout(() => {
      const handles = new Map<string, FileSystemFileHandle>(pendingHandles.current);
      for (const [id, resolved] of sources.current) if (resolved.handle) handles.set(id, resolved.handle);
      saveProject(project, handles)
        .then(() => saved.current.add(project.id))
        .catch((error: unknown) => {
          if (storageWarned.current) return;
          storageWarned.current = true;
          toast(
            `This project isn't being saved in the browser: ${error instanceof Error ? error.message : "storage failed"}. Use Save project to keep a file.`,
            "warning",
          );
        });
    }, 500);
    return () => clearTimeout(timer);
  }, [project, toast]);

  // Counts commits anywhere in the tree for the debug overlay. Profiler is a
  // no-op in production builds, so the number is only meaningful in dev.
  const onCommit = useCallback(() => diagnostics.current?.rendered(), []);

  const duration = projectDuration(project);
  const pps = BASE_PPS * zoom;

  /**
   * One loop drives everything: it reads the clock, brings the media elements
   * in line, and paints. Preview and export share renderFrame, so this is the
   * only place preview does anything of its own.
   */
  useEffect(() => {
    let frame = 0;
    const draw = () => {
      frame = requestAnimationFrame(draw);
      let at = clock.current.now();
      const stop = playing && at >= duration && duration > 0;
      if (stop) {
        clock.current.stop();
        clock.current.seek(duration);
        setPlaying(false);
        at = duration;
      }

      /**
       * While playing, the playhead is read from whichever element is keeping
       * time rather than from the wall, and the clock is nudged to match. This
       * is what stops the two fighting: a decoder that has fallen a little
       * behind is followed instead of being yanked forward, which was the
       * stutter after scrubbing during playback.
       *
       * The clock stays in charge for one stretch — after an explicit seek, or
       * on pressing play — until the elements have reached it. Otherwise the
       * first frame after a scrub would report the old position and undo the
       * scrub.
       */
      const master = playing && !stop ? pickMaster(project, at) : null;
      const masterElement = master
        ? master.kind === "video"
          ? pool.current?.videoFor(master.sourceId)
          : (pool.current?.audioFor(master.sourceId)?.element ?? null)
        : null;

      if (master && masterElement && canKeepTime(masterElement)) {
        const reported = timeFromElement(master, masterElement);
        if (clock.current.leads) {
          // Arrived: hand timekeeping over.
          if (Math.abs(reported - at) < HANDOVER_TOLERANCE) {
            clock.current.follow(reported);
            at = reported;
          }
        } else if (Number.isFinite(reported)) {
          clock.current.follow(reported);
          at = reported;
        }
      } else if (playing && !stop && clock.current.leads && !master) {
        // A gap in the timeline: nothing to wait for, so nothing to hold for.
        clock.current.follow(at);
      }
      if (clock.current.leads && clock.current.leadingFor() > MAX_HOLD_MS) {
        clock.current.follow(at);
      }

      playhead.advance(at);
      if (pool.current) {
        syncElements(project, pool.current, {
          playing: playing && !stop,
          playhead: at,
          authoritative: clock.current.leads,
          masterClipId: master?.id,
        });
      }
      const ctx = canvas.current?.getContext("2d", { alpha: false });
      if (ctx && pool.current) {
        renderFrame(ctx, { project, t: at, get: (id) => pool.current?.drawableFor(id) ?? null });
      }

      diagnostics.current?.frame({
        playhead: at,
        master: masterElement ?? null,
        clockLeads: clock.current.leads,
        audioState: pool.current?.audioState() ?? "no context",
      });
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [project, playing, duration, playhead]);

  useEffect(() => () => pool.current?.release(), []);

  const addFiles = useCallback(
    async (
      items: { file: File; handle?: FileSystemFileHandle }[],
      /** Fill this empty lane rather than opening a new one. */
      target?: { kind: "video" | "audio"; track: number },
    ) => {
      if (items.length === 0) return;
      setLoading(true);
      try {
        let first = true;
        for (const { file, handle } of items) {
          let ref;
          try {
            ref = await probeFile(file);
          } catch (error) {
            toast(error instanceof Error ? error.message : `Could not read ${file.name}`, "warning");
            continue;
          }
          sources.current.set(ref.id, {
            ref,
            file,
            url: URL.createObjectURL(file),
            ...(handle ? { handle } : {}),
          });
          // Only the first file can land in the lane that was clicked; the
          // rest still get their own, since stacking several into one lane is
          // never what picking several files meant.
          const lane = first ? target : undefined;
          first = false;
          setProject((current) => {
            // At the start, not after whatever is already there. Each file gets
            // a lane of its own, so parallel is the useful default: a music bed
            // or a reaction cam wants to run alongside the footage, and pushing
            // it to the end means dragging it back every time.
            const named =
              current.name === "Untitled" && current.sources.length === 0
                ? { ...current, name: ref.name.replace(/\.[^.]+$/, "") }
                : current;
            return addSource(named, ref, {
              start: 0,
              // A lane only takes the half of the file that belongs in it: an
              // audio-only file picked on a video lane still goes to audio.
              ...(lane?.kind === "video" ? { videoTrack: lane.track } : {}),
              ...(lane?.kind === "audio" ? { audioTrack: lane.track } : {}),
            }).project;
          });
          selectSource.current = ref.id;
        }
      } finally {
        setLoading(false);
      }
    },
    [toast],
  );

  const onDrop = useCallback(
    async (event: React.DragEvent) => {
      event.preventDefault();
      const entries = [...event.dataTransfer.items].filter((item) => item.kind === "file");
      const items = await Promise.all(
        entries.map(async (item) => {
          const file = item.getAsFile();
          if (!file) return null;
          // Asked for here, at import, because only a handle survives a reload
          // and only the drop event can still hand one out.
          const handle = await handleFromDrop(item);
          return { file, ...(handle ? { handle } : {}) };
        }),
      );
      await addFiles(items.filter((item): item is { file: File; handle?: FileSystemFileHandle } => item !== null));
    },
    [addFiles],
  );

  const seek = useCallback(
    (t: number) => {
      clock.current.seek(t);
      playhead.jump(clock.current.now());
    },
    [playhead],
  );

  const togglePlay = useCallback(() => {
    pool.current?.ensureAudioContext();
    if (clock.current.playing) {
      clock.current.stop();
      setPlaying(false);
      return;
    }
    if (clock.current.now() >= duration) clock.current.seek(0);
    clock.current.start();
    setPlaying(true);
  }, [duration]);

  /**
   * A title, at the playhead, on a text lane of its own. Nothing to probe and
   * nothing to load: the words are the clip.
   */
  const addText = useCallback(
    (track?: number) => {
      // The id is made out here, so the update below stays pure: React may run
      // it twice in development, and both runs must produce the same clip.
      const id = newId("c");
      setProject((current) =>
        addTextClip(current, { id, start: playhead.now, ...(track !== undefined ? { track } : {}) }).project,
      );
      setSelected(id);
      setFreshText(id);
    },
    [playhead],
  );

  /**
   * Recording lives alongside the transport: it starts the same clock the play
   * button does, and what it produces joins the project the way a dropped file
   * does. The file is written to disk by the recorder before it gets here, so
   * a handle may come with it and the project can reopen it later.
   */
  const recorder = useRecorder({
    onBegin: (playAlong) => {
      const at = clock.current.now();
      if (!playAlong || at >= duration) return { at, playStartedAt: null };
      pool.current?.ensureAudioContext();
      clock.current.start();
      setPlaying(true);
      return { at, playStartedAt: performance.now() };
    },
    onEnd: () => {
      clock.current.stop();
      setPlaying(false);
    },
    onTake: ({ saved, ref, start }) => {
      sources.current.set(ref.id, {
        ref,
        file: saved.file,
        url: URL.createObjectURL(saved.file),
        ...(saved.handle ? { handle: saved.handle } : {}),
      });
      setProject((current) => addSource(current, ref, { start }).project);
      selectSource.current = ref.id;
      toast(
        saved.how === "picker"
          ? `${ref.name} saved and added to the timeline.`
          : `${ref.name} downloaded and added to the timeline. A reopened project will ask for it, like any file.`,
        "success",
      );
    },
    onError: (message) => toast(message, "warning"),
  });
  const recording = recorder.state.phase !== "off";
  const takeInProgress = recorder.state.phase === "recording" || recorder.state.phase === "saving";

  const selectedClip = project.clips.find((clip) => clip.id === selected) ?? null;

  // The caret goes into a new title's words once. Selecting something else
  // ends that, so coming back to the title later does not grab the keyboard.
  useEffect(() => {
    if (selected !== freshText) setFreshText(null);
  }, [selected, freshText]);

  const runExport = useCallback(async (settings: ExportSettings) => {
    setExporting({ phase: "running", note: "Starting", fraction: 0 });
    const { exportProject } = await import("./export.ts");
    clock.current.stop();
    setPlaying(false);
    const controller = new AbortController();
    cancel.current = controller;
    try {
      const result = await exportProject({
        project,
        sources: sources.current,
        height: settings.height ?? undefined,
        fps: settings.fps,
        bitrate: videoBitrate(project, settings),
        signal: controller.signal,
        onProgress: (fraction, note) => setExporting({ phase: "running", fraction, note }),
      });
      const url = URL.createObjectURL(result.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      toast(
        `${result.filename} ready. ${formatBytes(result.blob.size)}, ${result.video}, ${(
          result.elapsed / 1000
        ).toFixed(1)}s${result.audio ? "" : ", no audio"}`,
        "success",
      );
      if (result.note) toast(result.note, "warning");
    } catch (error) {
      if ((error as DOMException)?.name === "AbortError") toast("Export cancelled");
      else toast(error instanceof Error ? error.message : "The export failed", "warning");
    } finally {
      setExporting(null);
      cancel.current = null;
    }
  }, [project, toast]);

  /**
   * Noise removal makes a new source from the clip's and lays a copy of the
   * clip over it on the track beneath, muting the original. Nothing is
   * replaced, so it is undoable and comparable, and the work runs off the main
   * thread, so playback carries on meanwhile.
   */
  const denoiseClip = useCallback(
    async (clip: Clip) => {
      const resolved = sources.current.get(clip.sourceId);
      if (!resolved) {
        toast("That clip's file isn't loaded, so there is nothing to clean yet.", "warning");
        return;
      }
      setDenoising({ clipId: clip.id, fraction: 0, note: "Starting" });
      try {
        const { denoiseSource } = await import("./denoise.ts");
        const cleaned = await denoiseSource(resolved, (fraction, note) =>
          setDenoising({ clipId: clip.id, fraction, note }),
        );
        sources.current.set(cleaned.ref.id, cleaned);
        setProject((current) => addCleanCopy(current, clip.id, cleaned.ref));
        toast("Noise removed. The clean copy is on the track below; the original is muted, not gone.", "success");
      } catch (error) {
        toast(error instanceof Error ? error.message : "Noise removal failed", "warning");
      } finally {
        setDenoising(null);
      }
    },
    [toast],
  );

  const restoreClip = useCallback((clip: Clip) => {
    setProject((current) => removeCleanCopy(current, clip.id));
  }, []);

  /**
   * Rebuild sources that were made here rather than loaded: a noise-removed
   * copy has no file behind it, so it is regenerated from its original once
   * that original is back.
   */
  const rebuildDerived = useCallback(
    async (current: Project) => {
      for (const source of current.sources) {
        if (source.derived?.op !== "denoise" || sources.current.has(source.id)) continue;
        const origin = sources.current.get(source.derived.from);
        if (!origin) continue;
        const clip = current.clips.find((entry) => entry.sourceId === source.id);
        try {
          const { denoiseSource } = await import("./denoise.ts");
          const rebuilt = await denoiseSource(
            origin,
            (fraction, note) => {
              if (clip) setDenoising({ clipId: clip.id, fraction, note: `Rebuilding: ${note}` });
            },
            undefined,
            source.id,
          );
          sources.current.set(source.id, rebuilt);
          refresh();
        } catch (error) {
          toast(`Could not rebuild ${source.name}: ${error instanceof Error ? error.message : "unknown error"}`, "warning");
        } finally {
          setDenoising(null);
        }
      }
    },
    [refresh, toast],
  );

  /** Swap the editor over to another project, with whatever files came with it. */
  const openProject = useCallback(
    (next: Project, supplied: Map<string, Supplied>, handles: Map<string, FileSystemFileHandle> = new Map()) => {
      clock.current.stop();
      setPlaying(false);
      pool.current?.release();
      pool.current = new MediaPool(sources.current);
      sources.current.clear();
      pendingHandles.current = new Map();
      const warnings: string[] = [];
      for (const [id, entry] of supplied) {
        sources.current.set(id, entry.resolved);
        const warning = matchWarning(entry.quality, entry.resolved.ref);
        if (warning) warnings.push(warning);
      }
      for (const [id, handle] of handles) if (!sources.current.has(id)) pendingHandles.current.set(id, handle);
      setProject(next);
      setSelected(null);
      setDialog(null);
      clock.current.seek(0);
      playhead.jump(0);
      for (const warning of warnings) toast(warning, "warning");
      void rebuildDerived(next);
    },
    [playhead, rebuildDerived, toast],
  );

  const showProjects = useCallback(async (start?: Staged) => {
    try {
      setDialog({ projects: storageAvailable() ? await listProjects() : [], ...(start ? { start } : {}) });
    } catch {
      setDialog({ projects: [], ...(start ? { start } : {}) });
    }
  }, []);

  const forgetProject = useCallback(
    async (id: string) => {
      try {
        await deleteProject(id);
        saved.current.delete(id);
        setDialog((current) => (current ? { ...current, projects: current.projects.filter((p) => p.id !== id) } : current));
      } catch (error) {
        toast(error instanceof Error ? error.message : "Could not forget that project", "warning");
      }
    },
    [toast],
  );

  /** A file for one specific source, whatever it turns out to be. */
  const relinkOne = useCallback(
    async (sourceId: string, picked?: Picked) => {
      const ref = sourceById(project, sourceId);
      if (!ref) return;
      const item = picked ?? (await pickMedia({ multiple: false }))[0];
      if (!item) return;
      const entry = await supply(ref, item);
      sources.current.set(sourceId, entry.resolved);
      pendingHandles.current.delete(sourceId);
      refresh();
      const warning = matchWarning(entry.quality, ref);
      if (warning) toast(warning, "warning");
      void rebuildDerived(project);
    },
    [project, refresh, rebuildDerived, toast],
  );

  /** A pile of files for whatever is missing, matched by fingerprint then name. */
  const relinkMany = useCallback(async () => {
    const missing = project.sources.filter((entry) => !entry.derived && !sources.current.has(entry.id));
    const picked = await pickMedia({ multiple: true });
    if (picked.length === 0) return;
    const { matched, unmatched } = await assign(missing, picked);
    for (const [id, entry] of matched) {
      sources.current.set(id, entry.resolved);
      pendingHandles.current.delete(id);
      const warning = matchWarning(entry.quality, entry.resolved.ref);
      if (warning) toast(warning, "warning");
    }
    refresh();
    if (unmatched.length > 0) {
      toast(
        `${unmatched.map((file) => file.name).join(", ")} ${unmatched.length === 1 ? "doesn't" : "don't"} match any missing file. Choose it on its track to use it anyway.`,
        "warning",
      );
    }
    void rebuildDerived(project);
  }, [project, refresh, rebuildDerived, toast]);

  /** Reopen missing files from their stored handles, with the browser's permission. */
  const reopenPending = useCallback(async () => {
    const missing = project.sources.filter((entry) => !entry.derived && !sources.current.has(entry.id));
    const got = await reopen(missing, pendingHandles.current);
    for (const [id, entry] of got) {
      sources.current.set(id, entry.resolved);
      pendingHandles.current.delete(id);
    }
    refresh();
    if (got.size < missing.length) toast(`${missing.length - got.size} still to choose by hand.`);
    void rebuildDerived(project);
  }, [project, refresh, rebuildDerived, toast]);

  const saveProjectFile = useCallback(() => {
    const blob = new Blob([serializeProject(project)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${project.name.replace(/\s+/g, "-").toLowerCase() || "project"}.otterstudio.json`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
    toast("Project file saved. It holds the edit, not the media.");
  }, [project, toast]);

  const empty = project.clips.length === 0;
  const audioOnly = isAudioOnly(project);
  // The video encoder's caveats do not apply to a sound-only export.
  const blocked = audioOnly
    ? caps.checked && !caps.audio
      ? "This browser cannot encode audio, so it cannot export sound. Chrome, Edge or Safari can."
      : null
    : exportBlockedReason(caps);

  return (
    <Profiler id="studio" onRender={onCommit}>
    <div className="grain relative flex min-h-dvh flex-col bg-cream font-sans text-ink">
      {/* The export owns the media elements while it runs, so the editor is
          taken out of reach: no focus, no pointer, no keyboard. */}
      <div
        inert={exporting ? true : undefined}
        className="relative z-10 mx-auto flex min-h-dvh w-full max-w-[1400px] flex-col px-4 pb-8 sm:px-6 lg:px-8"
      >
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-rule py-4">
          <div className="flex min-w-0 items-center gap-3">
            <h1 className="flex shrink-0 items-center gap-2.5 font-serif text-[26px] font-semibold leading-none text-pine sm:text-[30px]">
              <img src="/logo.png" alt="" width="30" height="30" className="size-[30px]" />
              OtterStudio
              <span className="sr-only">: trim, layer and export video in your browser</span>
            </h1>
            {empty ? null : (
              <>
                <span aria-hidden="true" className="text-[22px] font-light text-rule">/</span>
                <input
                  aria-label="Project name"
                  value={project.name}
                  size={Math.max(6, Math.min(32, project.name.length + 1))}
                  onChange={(event) => setProject({ ...project, name: event.target.value, updatedAt: Date.now() })}
                  className="min-w-0 rounded-[6px] border border-transparent bg-transparent px-1.5 py-0.5 font-serif text-[18px] text-ink transition-colors hover:border-rule focus:border-pine focus:outline-none"
                />
              </>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => void showProjects()} className="pill">
              Projects
            </button>
            <AddFilesButton
              solid={empty}
              label={empty ? "Add video, image or audio" : "Add file"}
              onFiles={(items) => void addFiles(items)}
            />
            <AddTextButton onClick={() => addText()} />
            <button type="button" onClick={saveProjectFile} disabled={empty} className="pill">
              Save project
            </button>
            <button
              type="button"
              onClick={() => setExporting({ phase: "options" })}
              disabled={empty || Boolean(exporting) || takeInProgress || (audioOnly ? !caps.audio : !caps.video)}
              className="pill-solid"
            >
              {audioOnly ? "Export M4A" : "Export MP4"}
            </button>
          </div>
        </header>

        {blocked ? (
          <p className="mt-4 rounded-[10px] border border-warn/40 bg-warn/10 px-4 py-2.5 text-[13px] leading-[1.5] text-ink">
            {blocked}
          </p>
        ) : null}

        <div
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => void onDrop(event)}
          className="mt-4 grid flex-1 gap-4 lg:grid-cols-[minmax(0,1fr)_300px]"
        >
          <div className="flex min-w-0 flex-col gap-3">
            <Preview
              ref={canvas}
              project={project}
              empty={empty && !recording}
              loading={loading}
              onPickFiles={(items) => void addFiles(items)}
            >
              {empty ? null : (
                <Stage
                  project={project}
                  playhead={playhead}
                  selected={selected}
                  canvasRef={canvas}
                  interactive={!exporting && !playing && !takeInProgress}
                  onSelect={setSelected}
                  onChange={setProject}
                />
              )}
              <CameraPeek
                stream={recorder.state.stream}
                videoRef={recorder.videoRef}
                recording={recorder.state.phase === "recording"}
                large={empty}
              />
            </Preview>
            <Transport
              playing={playing}
              playhead={playhead}
              duration={duration}
              locked={takeInProgress}
              armed={recording}
              recordable={canRecord()}
              onToggle={togglePlay}
              onSeek={seek}
              onRecord={recording ? recorder.actions.disarm : recorder.actions.arm}
            />
            {recording ? (
              <RecordStrip state={recorder.state} actions={recorder.actions} hasTimeline={duration > 0} />
            ) : null}
            <Timeline
              project={project}
              pps={pps}
              playhead={playhead}
              selected={selected}
              resolved={sources.current}
              onSelect={setSelected}
              onSeek={seek}
              onChange={setProject}
              onAddFiles={(items, target) => void addFiles(items, target)}
              onAddText={addText}
              onRelink={(sourceId) => void relinkOne(sourceId)}
              zoom={zoom}
              onZoom={setZoom}
            />
          </div>

          <Inspector
            project={project}
            clip={selectedClip}
            freshText={freshText}
            playhead={playhead}
            resolved={sources.current}
            caps={caps}
            denoising={denoising}
            onDenoise={(clip) => void denoiseClip(clip)}
            onRestore={restoreClip}
            onChange={setProject}
            onSelect={setSelected}
            onRelink={(sourceId) => void relinkOne(sourceId)}
            onRelinkMany={() => void relinkMany()}
            onReopen={() => void reopenPending()}
            canReopen={canPersistHandles() && pendingHandles.current.size > 0}
          />
        </div>

        {/* Ad: directly under the tool. Renders nothing until a client id, slot and consent all exist. */}
        <AdBanner
          format="responsive"
          slot={import.meta.env["VITE_ADSENSE_SLOT"] as string | undefined}
          className="mx-auto mt-16 max-w-[728px]"
        />

        {/* What this is, how it works, FAQ. Static, also prerendered for crawlers. */}
        <Explainer />

        <Footer />
      </div>
      {dialog ? (
        <OpenDialog
          projects={dialog.projects}
          {...(dialog.start ? { start: dialog.start } : {})}
          load={loadProject}
          onNew={() => openProject(emptyProject(), new Map())}
          onOpen={(next, supplied) => {
            // Handles come from the stored record the dialog loaded; ask it.
            void loadProject(next.id)
              .then((stored) => openProject(next, supplied, stored?.handles ?? new Map()))
              .catch(() => openProject(next, supplied));
          }}
          onDelete={(id) => void forgetProject(id)}
          {...(empty && !saved.current.has(project.id) && dialog.projects.length > 0 && !dialog.start
            ? {}
            : { onClose: () => setDialog(null) })}
        />
      ) : null}
      {exporting ? (
        <ExportDialog
          project={project}
          duration={duration}
          missing={project.sources.filter((entry) => !sources.current.has(entry.id)).map((entry) => entry.name)}
          settings={exportSettings ?? defaultSettings(project)}
          onSettings={setExportSettings}
          state={exporting}
          onStart={(settings) => void runExport(settings)}
          onCancel={() => cancel.current?.abort()}
          onClose={() => setExporting(null)}
        />
      ) : null}
    </div>
    </Profiler>
  );
}

/* ---- preview ------------------------------------------------------------- */

/** The preview with nothing wired to it, for dev/sound.html. */
export function PreviewForDev({
  ref,
  project,
}: {
  ref: React.RefObject<HTMLCanvasElement | null>;
  project: Project;
}) {
  return <Preview ref={ref} project={project} empty={false} loading={false} onPickFiles={() => undefined} />;
}

/** What stands in for the picture when the timeline holds nothing but sound. */
function SoundOnlyPanel({ project }: { project: Project }) {
  const names = [...new Set(project.sources.map((source) => source.name))];
  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-surface text-center"
      style={{
        backgroundImage:
          "repeating-linear-gradient(90deg, transparent 0 22px, rgb(45 85 78 / 0.05) 22px 23px)",
      }}
    >
      <span className="flex size-14 items-center justify-center rounded-full border border-rule bg-cream text-pine shadow-[0_2px_0_rgb(51_41_31/0.06)]">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 18V5l12-2v13" />
          <circle cx="6" cy="18" r="3" />
          <circle cx="18" cy="16" r="3" />
        </svg>
      </span>
      <span className="font-serif text-[20px] font-semibold text-pine sm:text-[22px]">Sound only</span>
      <span className="max-w-[60ch] truncate px-6 text-[13px] text-soft">
        {names.length === 1 ? names[0] : `${names.length} files`}
      </span>
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
        Exports as an M4A file
      </span>
    </div>
  );
}

interface PreviewProps {
  project: Project;
  empty: boolean;
  loading: boolean;
  onPickFiles: (items: Picked[]) => void;
  ref: React.RefObject<HTMLCanvasElement | null>;
  /** The transform handles, laid over the canvas rather than drawn into it. */
  children?: ReactNode;
}

/** The most of the viewport's height the stage may take. */
const PREVIEW_MAX_VH = 68;

function Preview({ ref, project, empty, loading, onPickFiles, children }: PreviewProps) {
  const inputId = "studio-files";
  const audioOnly = isAudioOnly(project);
  // Lit while a file is held over the empty stage. Enter and leave fire for
  // every child, so a plain boolean flickers; the pairs are counted instead.
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);
  // Sound alone gets a short panel rather than a black picture-shaped hole.
  const aspect = audioOnly ? "16 / 5" : `${project.width} / ${project.height}`;

  /**
   * The preview canvas is the size it is displayed at, not the size of the
   * composition.
   *
   * It used to be the composition: a 3840x2160 project meant a 3840x2160
   * backing store, eight megapixels redrawn sixty times a second to be shown
   * a thousand pixels wide. Measured at p95 55ms per frame and 40fps on a 4K
   * clip. renderFrame already scales to whatever canvas it is given, and the
   * export draws on its own full-size canvas, so nothing else changes.
   *
   * Capped at the composition's size so a small clip on a big display is not
   * upscaled past what it has, and at 2x device pixels because beyond that the
   * cost is real and the difference is not.
   */
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const size = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.min(project.width, Math.round(canvas.clientWidth * dpr));
      const height = Math.min(project.height, Math.round(canvas.clientHeight * dpr));
      if (width > 0 && height > 0 && (canvas.width !== width || canvas.height !== height)) {
        canvas.width = width;
        canvas.height = height;
      }
    };
    size();
    const observer = new ResizeObserver(size);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [ref, project.width, project.height]);

  /**
   * A tall frame is sized by height, a wide one by width.
   *
   * Left to the column, a 9:16 preview is as wide as a 16:9 one and nearly
   * twice the height of the screen: the timeline is a page-scroll away and the
   * whole title is never in view. So the stage may not be taller than most of
   * the viewport, and its width follows from that through the aspect ratio.
   * The mat still spans the column, so the frame sits centred on it.
   */
  const stageWidth = `min(100%, ${(PREVIEW_MAX_VH * project.width) / project.height}vh)`;

  return (
    <div
      className={`relative flex justify-center overflow-hidden rounded-[14px] border border-rule ${
        empty ? "bg-cream" : "mat"
      }`}
      onDragEnter={(event) => {
        event.preventDefault();
        if (!empty) return;
        depth.current += 1;
        setDragging(true);
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        depth.current -= 1;
        if (depth.current <= 0) {
          depth.current = 0;
          setDragging(false);
        }
      }}
      onDrop={() => {
        depth.current = 0;
        setDragging(false);
      }}
    >
      <div className="relative" style={{ width: audioOnly ? "100%" : stageWidth }}>
        {/* The canvas still paints its dark background every frame; while the
            stage is empty it only sets the frame's shape, so it is hidden. */}
        <canvas
          ref={ref}
          className={`block h-auto w-full ${empty ? "invisible" : ""}`}
          style={{ aspectRatio: aspect } as CSSProperties}
        />
        {children}
        {/* The same drop zone OtterDrop opens with, drawn inside the frame so
            it is the frame's shape: a tall composition gets a tall zone and
            the words wrap to it rather than running across the whole mat. */}
        {empty ? (
          <label
            htmlFor={inputId}
            className={`absolute inset-3 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-[18px] border-2 border-dashed px-6 py-8 text-center transition-colors sm:inset-4 ${
              dragging ? "border-teal bg-teal/12" : "border-teal/45 bg-teal/[0.06] hover:border-teal/70"
            }`}
          >
            <span className="text-balance font-serif text-[21px] font-semibold leading-tight text-pine sm:text-[30px]">
              {loading ? "Reading your file…" : "Drop a media file here to begin"}
            </span>
            <span className="text-[13px] text-soft">
              or <span className="link font-medium text-teal-deep">choose from this device</span>
            </span>
            <span className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
              Nothing is uploaded
            </span>
          </label>
        ) : null}
      </div>
      {audioOnly ? <SoundOnlyPanel project={project} /> : null}
      <input
        id={inputId}
        type="file"
        accept={ACCEPT}
        multiple
        className="sr-only"
        onChange={(event) => {
          onPickFiles([...(event.target.files ?? [])].map((file) => ({ file })));
          event.target.value = "";
        }}
      />
    </div>
  );
}

/**
 * Adding a file.
 *
 * A drop zone alone is not discoverable: it only shows while the timeline is
 * empty, so the way to add a second file was to guess that dragging onto the
 * page still worked. A label wrapping a file input is the same control in both
 * places and needs no state of its own.
 */
/**
 * Goes through pickMedia rather than an <input type=file>, because the
 * File System Access picker is the only way to get a handle, and a handle is
 * the only way a reopened project can offer to open its own files.
 */
function AddFilesButton({
  label,
  solid = false,
  onFiles,
}: {
  label: string;
  solid?: boolean;
  onFiles: (items: Picked[]) => void;
}) {
  return (
    <button
      type="button"
      className={solid ? "pill-solid" : "pill"}
      onClick={() => void pickMedia({ multiple: true }).then((items) => items.length > 0 && onFiles(items))}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
        <path d="M12 5v14" />
        <path d="M5 12h14" />
      </svg>
      {label}
    </button>
  );
}

/** The other way to put something on the timeline: a title, from nothing. */
function AddTextButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="pill" onClick={onClick}>
      <TextIcon />
      Add text
    </button>
  );
}

const TextIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 6V4h14v2" />
    <path d="M12 4v16" />
    <path d="M9 20h6" />
  </svg>
);

const ImageIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <circle cx="9" cy="10" r="1.6" />
    <path d="m21 16-5-5-9 9" />
  </svg>
);

/* ---- export dialog ------------------------------------------------------- */

export type ExportState = null | { phase: "options" } | { phase: "running"; note: string; fraction: number };

/**
 * Two screens in one dialog: the settings, which can be dismissed, and the
 * running export, which cannot. While it runs the only way out is Cancel,
 * which stops the export; there is no closing it and carrying on editing.
 */
export function ExportDialog({
  project,
  duration,
  missing,
  settings,
  onSettings,
  state,
  onStart,
  onCancel,
  onClose,
}: {
  project: Project;
  duration: number;
  /** Names of sources whose files aren't loaded; they export as placeholders. */
  missing: string[];
  settings: ExportSettings;
  onSettings: (settings: ExportSettings) => void;
  state: NonNullable<ExportState>;
  onStart: (settings: ExportSettings) => void;
  onCancel: () => void;
  onClose: () => void;
}) {
  const titleId = useId();
  const primary = useRef<HTMLButtonElement>(null);
  const running = state.phase === "running";

  useEffect(() => {
    primary.current?.focus();
  }, [running]);

  // Escape dismisses the settings, and nothing else.
  useEffect(() => {
    if (running) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [running, onClose]);

  const size = outputSize(project, settings);
  const hasAudio = project.clips.some((clip) => clip.kind === "audio" && !clip.muted);
  const audioOnly = isAudioOnly(project);
  const estimate = audioOnly
    ? estimatedAudioBytes(duration)
    : estimatedBytes(project, settings, duration, hasAudio);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={running || undefined}
        className="w-full max-w-[400px] rounded-[14px] border border-rule bg-surface p-5 shadow-[0_18px_48px_rgb(51_41_31/0.28)]"
      >
        {running ? (
          <>
            <h2 id={titleId} className="font-serif text-[20px] font-semibold leading-tight text-pine">
              Exporting
            </h2>
            <span className="mt-1 block font-mono text-[11px] text-muted">
              {audioOnly ? "AAC · 128 kbit/s" : `${size.width}×${size.height} · ${settings.fps} fps`}
            </span>
            <OtterSays picture="at-laptop">{exportLine(state.fraction, audioOnly)}</OtterSays>
            <div className="mt-4 flex items-center gap-3">
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-cream-2">
                <div
                  className="h-full rounded-full bg-pine transition-[width]"
                  style={{ width: `${state.fraction * 100}%` }}
                />
              </div>
              <span className="w-[4ch] text-right font-mono text-[12px] tabular-nums text-ink">
                {Math.round(state.fraction * 100)}%
              </span>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3">
              <span className="font-mono text-[11px] text-muted">{state.note}</span>
              <button ref={primary} type="button" onClick={onCancel} className="pill">
                Cancel export
              </button>
            </div>
            {/* Ad: an export is the one place people sit and wait. Renders nothing
                until a client id, a slot and consent all exist. Its own slot, so
                the two units on the page are not one unit twice. */}
            <AdBanner
              format="rectangle"
              slot={
                (import.meta.env["VITE_ADSENSE_SLOT_EXPORT"] as string | undefined) ||
                (import.meta.env["VITE_ADSENSE_SLOT"] as string | undefined)
              }
              className="mx-auto mt-5 max-w-[300px] border-t border-rule pt-4"
            />
          </>
        ) : (
          <>
            <h2 id={titleId} className="font-serif text-[20px] font-semibold leading-tight text-pine">
              {audioOnly ? "Export M4A" : "Export MP4"}
            </h2>
            <p className="mt-1 text-[13px] leading-[1.5] text-soft">
              {audioOnly
                ? `${timecode(duration)} of sound, no video. AAC in an M4A file, which plays wherever an MP3 does.`
                : `${timecode(duration)} of video${hasAudio ? " with sound" : ", no sound"}.`}
            </p>

            {audioOnly ? (
              <span className="mt-3 block font-mono text-[11px] text-muted">
                AAC · 128 kbit/s · about {formatBytes(estimate)}
              </span>
            ) : (
            <div className="mt-4 flex flex-col gap-3.5">
              <div>
                <span className="eyebrow block mb-1.5">Resolution</span>
                <Segmented
                  label="Resolution"
                  options={resolutionChoices(project).map((choice) => ({
                    value: choice.height,
                    label: choice.height ? `${choice.height}p` : "Original",
                    hint: choice.label,
                  }))}
                  selected={settings.height}
                  onSelect={(height) => onSettings({ ...settings, height })}
                />
                <span className="mt-1 block font-mono text-[11px] text-muted">
                  {size.width}×{size.height}
                </span>
              </div>
              {frameRateChoices(project).length > 1 ? (
                <div>
                  <span className="eyebrow block mb-1.5">Frame rate</span>
                  <Segmented
                    label="Frame rate"
                    options={frameRateChoices(project).map((fps) => ({
                      value: fps,
                      label: `${fps} fps`,
                      hint: fps === project.fps ? "The composition's own rate" : `Every frame resampled to ${fps} a second`,
                    }))}
                    selected={settings.fps}
                    onSelect={(fps) => onSettings({ ...settings, fps })}
                  />
                </div>
              ) : null}
              <div>
                <span className="eyebrow block mb-1.5">Quality</span>
                <Segmented<Quality>
                  label="Quality"
                  options={[
                    { value: "small", label: "Smaller file", hint: "Lower bitrate. Fine for messaging and previews." },
                    { value: "standard", label: "Standard", hint: "Looks right without producing a file nobody can send." },
                    { value: "high", label: "High", hint: "Higher bitrate. For uploads that get re-encoded anyway." },
                  ]}
                  selected={settings.quality}
                  onSelect={(quality) => onSettings({ ...settings, quality })}
                />
                <span className="mt-1 block font-mono text-[11px] text-muted">
                  {(videoBitrate(project, settings) / 1_000_000).toFixed(1)} Mbit/s · up to about {formatBytes(estimate)}
                </span>
              </div>
            </div>
            )}

            {missing.length > 0 ? (
              <p className="mt-4 rounded-[8px] border border-warn/40 bg-warn/10 px-3 py-2 text-[12px] leading-[1.5] text-ink">
                <span className="font-medium">Some files aren't loaded:</span> {missing.join(", ")}. Their clips
                would export as grey placeholders and silence. Choose them from the timeline first, unless
                that is what you want.
              </p>
            ) : null}

            <div className="mt-5 flex items-center justify-end gap-2">
              <button ref={missing.length > 0 ? primary : undefined} type="button" onClick={onClose} className="pill">
                Cancel
              </button>
              <button
                ref={missing.length > 0 ? undefined : primary}
                type="button"
                onClick={() => onStart(settings)}
                className={missing.length > 0 ? "pill" : "pill-solid"}
              >
                {missing.length > 0 ? "Export anyway" : "Start export"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * What the otter says while the export runs. Progress changes the line, so a
 * long export reads as moving even between percentage ticks, and the one
 * instruction that matters — keep the tab in front — is repeated rather than
 * shown once at the top and scrolled past.
 */
export function exportLine(fraction: number, audioOnly: boolean): string {
  if (audioOnly) return "Mixing the sound and encoding it to AAC. This is quick, no need to wait around.";
  if (fraction < 0.05) return "Warming up the encoder. Keep this tab in front: I draw the frames from the preview, so it has to stay visible.";
  if (fraction < 0.5) return "Drawing every frame and handing it to your video hardware. Keep this tab in front while I work.";
  if (fraction < 0.9) return "Past halfway. Same as before: this tab stays in front, and I keep drawing.";
  return "Nearly there. Finishing the last frames and writing the file.";
}

/* ---- transport ----------------------------------------------------------- */

function Transport({
  playing,
  playhead,
  duration,
  locked,
  armed,
  recordable,
  onToggle,
  onSeek,
  onRecord,
}: {
  playing: boolean;
  playhead: PlayheadStore;
  duration: number;
  /** A take is running: the clock is the recorder's, not the play button's. */
  locked: boolean;
  /** The recording strip is open. */
  armed: boolean;
  recordable: boolean;
  onToggle: () => void;
  onSeek: (t: number) => void;
  onRecord: () => void;
}) {
  const slider = useRef<HTMLInputElement>(null);
  const readout = useRef<HTMLSpanElement>(null);
  // While a pointer holds the thumb, the thumb is the pointer's. A range input
  // keeps focus after a click, so focus is the wrong thing to check: the slider
  // would freeze for the rest of playback.
  const held = useRef(false);

  // Moved every frame, so moved by hand rather than through React.
  useEffect(
    () =>
      playhead.target((seconds) => {
        if (slider.current && !held.current) {
          slider.current.value = String(Math.min(seconds, duration));
        }
        if (readout.current) readout.current.textContent = timecode(seconds);
      }),
    [playhead, duration],
  );

  return (
    <div className="flex items-center gap-3 rounded-[12px] border border-rule bg-surface px-3 py-2.5">
      <button
        type="button"
        onClick={onToggle}
        disabled={duration === 0 || locked}
        aria-label={playing ? "Pause" : "Play"}
        className="flex size-9 shrink-0 items-center justify-center rounded-full border-2 border-pine bg-pine text-cream disabled:opacity-40"
      >
        {playing ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <rect x="6" y="5" width="4" height="14" rx="1" />
            <rect x="14" y="5" width="4" height="14" rx="1" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M8 5.5v13l11-6.5z" />
          </svg>
        )}
      </button>
      {recordable ? (
        <button
          type="button"
          onClick={onRecord}
          disabled={locked}
          aria-label={armed ? "Close recording" : "Record from camera or microphone"}
          aria-pressed={armed}
          title={armed ? "Close recording" : "Record from camera or microphone"}
          className={`flex size-9 shrink-0 items-center justify-center rounded-full border-2 transition-colors disabled:opacity-40 ${
            armed ? "border-danger bg-danger text-cream" : "border-rule bg-surface text-danger hover:border-danger"
          }`}
        >
          <span aria-hidden="true" className="block size-3 rounded-full bg-current" />
        </button>
      ) : null}
      <input
        ref={slider}
        type="range"
        className="scrub flex-1"
        min={0}
        max={Math.max(duration, 0.001)}
        step={0.01}
        defaultValue={Math.min(playhead.now, duration)}
        onPointerDown={() => {
          held.current = true;
        }}
        onPointerUp={() => {
          held.current = false;
        }}
        onPointerCancel={() => {
          held.current = false;
        }}
        onChange={(event) => onSeek(Number(event.target.value))}
        aria-label="Playhead"
      />
      <span className="shrink-0 font-mono text-[12px] tabular-nums text-soft">
        <span ref={readout}>{timecode(playhead.now)}</span> / {timecode(duration)}
      </span>
    </div>
  );
}

/* ---- timeline ------------------------------------------------------------ */

/** Slim enough that a ruler, two lanes and their breathing room fit a laptop screen. */
const LANE_HEIGHT = "var(--lane-height)";
/** Between the ruler and the first lane, and between lanes: room for the selection ring and then some. */
const LANE_GAP = "gap-2.5";
/**
 * The selected clip's ring is drawn outside its box (a 2px outline, 1px off).
 * A clip at zero sits flush with the scroller's left edge, where the scroller
 * clips overflow, so the ring's left side was cut off. The lanes are inset by
 * this much so there is room for it; the ruler and the marker are laid out
 * inside the same inset, so nothing else moves relative to anything.
 */
const SELECTION_RING = 3;

function Timeline({
  project,
  pps,
  playhead,
  selected,
  resolved,
  onSelect,
  onSeek,
  onChange,
  onAddFiles,
  onAddText,
  onRelink,
  zoom,
  onZoom,
}: {
  project: Project;
  pps: number;
  playhead: PlayheadStore;
  selected: string | null;
  resolved: Map<string, ResolvedSource>;
  onSelect: (id: string | null) => void;
  onSeek: (t: number) => void;
  onChange: (project: Project) => void;
  onAddFiles: (items: Picked[], target?: { kind: "video" | "audio"; track: number }) => void;
  /** A title at the playhead, on a new text lane or the given empty one. */
  onAddText: (track?: number) => void;
  /** Supply the file for a source whose file is missing. */
  onRelink: (sourceId: string) => void;
  zoom: number;
  onZoom: (zoom: number) => void;
}) {
  const duration = projectDuration(project);

  // A line that steps at a twelfth of a second reads as a stutter, so this one
  // is positioned by hand every frame.
  const marker = useRef<HTMLDivElement>(null);
  useEffect(
    () =>
      playhead.target((seconds) => {
        if (marker.current) marker.current.style.left = `${seconds * pps}px`;
      }),
    [playhead, pps],
  );

  /**
   * Every lane from 0 to the highest in use, not only the ones with clips in
   * them. A lane that empties out by deleting its last clip should stay where
   * it is rather than making everything below it jump up a row.
   *
   * Video is listed top-down so the lane drawn on top is the row on top, which
   * is the way every editor does it and the opposite of the array order.
   */
  const lanes = useMemo(() => {
    const text = topTrack(project, "text");
    const video = topTrack(project, "video");
    const audio = topTrack(project, "audio");
    const rows: { kind: ClipKind; track: number }[] = [];
    // Titles draw over everything, so their lanes sit over everything too.
    for (let track = text; track >= 0; track -= 1) rows.push({ kind: "text", track });
    for (let track = video; track >= 0; track -= 1) rows.push({ kind: "video", track });
    for (let track = 0; track <= audio; track += 1) rows.push({ kind: "audio", track });
    // An empty project still shows the shape it is going to have: one video
    // lane and one audio lane, waiting. A timeline that appears only once it
    // has something in it reads as though it were missing.
    return rows.length > 0
      ? rows
      : [
          { kind: "video" as const, track: 0 },
          { kind: "audio" as const, track: 0 },
        ];
  }, [project]);

  // Enough room for the edit plus a little tail to drag into; min-w-full on the
  // element then stretches it to the container when the edit is shorter than
  // the panel, so empty lanes span the width instead of stopping short.
  const contentWidth = duration * pps + 80;

  const scroller = useRef<HTMLDivElement>(null);
  const [view, setView] = useState({ width: 0, scrollLeft: 0 });
  const measureView = useCallback(() => {
    const element = scroller.current;
    if (element) setView({ width: element.clientWidth, scrollLeft: element.scrollLeft });
  }, []);
  useEffect(() => {
    const element = scroller.current;
    if (!element) return;
    measureView();
    const observer = new ResizeObserver(measureView);
    observer.observe(element);
    return () => observer.disconnect();
  }, [measureView]);

  /**
   * Never shrink below what is currently on screen.
   *
   * A timeline sized purely by its content shrinks when an edit gets shorter,
   * and if the view is scrolled into the part that vanished, the browser clamps
   * scrollLeft to fit — which reads as the whole timeline jumping sideways.
   * That is what happened on releasing a drag: the previous fix froze the width
   * for the length of the gesture and then let go of it all at once.
   *
   * Anchoring to the scroll position instead is the invariant that was wanted
   * all along, and it holds whether or not anything is being dragged. The
   * element keeps at least enough width for where we are looking, so there is
   * never anything to clamp, and it gives the width back as the view scrolls
   * left again.
   */
  const width = Math.max(contentWidth, view.scrollLeft + view.width - SELECTION_RING);

  // The ruler is drawn across the width the lanes actually occupy, not across
  // the duration: a stretched lane whose ticks stop after one second looks
  // broken, and an empty timeline has no duration at all.
  const span = width / pps;

  return (
    <div className="flex flex-col gap-2 rounded-[12px] border border-rule bg-surface p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="eyebrow">Timeline</span>
          <AddFilesButton label="Add file" onFiles={onAddFiles} />
          <AddTextButton onClick={() => onAddText()} />
        </div>
        <label className="flex items-center gap-2 text-[11px] text-muted">
          Zoom
          <input
            type="range"
            min={0.25}
            max={4}
            step={0.25}
            value={zoom}
            onChange={(event) => onZoom(Number(event.target.value))}
            className="fader w-24"
          />
        </label>
      </div>

      <div className="flex">
          {/* The labels do not scroll with the lanes, so they stay readable on a
              long edit. The spacer matches the ruler's height. */}
          <div className="w-[112px] shrink-0 pr-2 sm:w-[186px]">
            <div className="mb-3 h-6" />
            <div className={`flex flex-col ${LANE_GAP}`}>
              {lanes.map(({ kind, track }) => (
                <TrackHead
                  key={`head-${kind}-${track}`}
                  project={project}
                  kind={kind}
                  track={track}
                  onChange={onChange}
                  onAddFiles={onAddFiles}
                  onAddText={onAddText}
                  missing={kind !== "text" && clipsInTrack(project, kind, track).some((clip) => !resolved.has(clip.sourceId))}
                  onRelink={onRelink}
                />
              ))}
            </div>
          </div>

          <div
            ref={scroller}
            onScroll={measureView}
            className="scroll-thin relative min-w-0 flex-1 overflow-x-auto [overscroll-behavior-x:contain]"
            style={{ paddingLeft: SELECTION_RING, paddingBottom: SELECTION_RING + 5 }}
            // A press on the empty part of a lane lets go of the selection. The
            // ruler is not a lane: scrubbing to a split point must keep the clip.
            onPointerDown={(event) => {
              const target = event.target as HTMLElement;
              if (!target.closest(".lane") || target.closest(".clip")) return;
              onSelect(null);
            }}
          >
            {project.clips.length === 0 ? (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 top-7 z-10 flex items-center justify-center px-4">
                <p className="text-balance text-center font-serif text-[16px] italic text-muted">
                  Your clips will line up here.
                </p>
              </div>
            ) : null}
            <div style={{ width }} className="relative min-w-full">
              <Ruler span={span} pps={pps} onSeek={onSeek} />
              <div className={`flex flex-col ${LANE_GAP}`}>
                {lanes.map(({ kind, track }) => {
                  const inLane = project.clips.filter(
                    (clip) => clip.kind === kind && clip.track === track,
                  );
                  return (
                    <div
                      key={`${kind}-${track}`}
                      className={`lane relative rounded-[8px] bg-cream-2/40 ${
                        inLane.length === 0
                          ? "border border-dashed border-rule"
                          : "border border-rule/70"
                      }`}
                      style={{ height: LANE_HEIGHT, backgroundSize: `${pps}px 100%` }}
                    >
                      {inLane.map((clip) => (
                        <ClipBlock
                          key={clip.id}
                          project={project}
                          clip={clip}
                          pps={pps}
                          selected={selected === clip.id}
                          missing={clip.kind !== "text" && !resolved.has(clip.sourceId)}
                          scroller={scroller}
                          onSelect={onSelect}
                          onChange={onChange}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
              {/* The playhead is meaningless before there is anything to play. */}
              {project.clips.length > 0 ? (
                <div
                  ref={marker}
                  aria-hidden="true"
                  className="pointer-events-none absolute bottom-0 top-0 w-px bg-danger"
                  style={{ left: playhead.now * pps }}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
  );
}

function Ruler({ span, pps, onSeek }: { span: number; pps: number; onSeek: (t: number) => void }) {
  // A tick every second is unreadable when zoomed out and too sparse when in.
  const step = pps > 120 ? 0.5 : pps > 48 ? 1 : pps > 20 ? 5 : 10;
  const ticks: number[] = [];
  // Up to the span, not past it. A tick beyond the visible width is absolutely
  // positioned, and absolutely positioned children still count towards a scroll
  // container's overflow — which is why an empty timeline had a scrollbar.
  for (let t = 0; t <= span; t += step) ticks.push(t);
  return (
    <div
      // Clipped as well, so the last label's own text cannot push the scroll
      // width out by its few pixels.
      className="relative mb-3 h-6 cursor-pointer overflow-hidden border-b border-rule"
      onPointerDown={(event) => {
        const box = event.currentTarget.getBoundingClientRect();
        onSeek(Math.max(0, (event.clientX - box.left) / pps));
      }}
    >
      {ticks.map((t) => (
        <span
          key={t}
          className="absolute bottom-0.5 font-mono text-[9px] text-muted"
          style={{ left: t * pps + 2 }}
        >
          {timecode(t)}
        </span>
      ))}
    </div>
  );
}

/**
 * A lane's own controls.
 *
 * Volume and mute live here rather than only in the clip inspector because a
 * level is a property of a track in every editor anyone has used, and after a
 * split one lane holds several clips that should still move together. Video
 * lanes get no fader: their sound is a separate audio clip, and a slider that
 * did nothing would be worse than none.
 */
function TrackHead({
  project,
  kind,
  track,
  onChange,
  onAddFiles,
  onAddText,
  missing,
  onRelink,
}: {
  project: Project;
  kind: ClipKind;
  track: number;
  onChange: (project: Project) => void;
  onAddFiles: (items: Picked[], target?: { kind: "video" | "audio"; track: number }) => void;
  onAddText: (track?: number) => void;
  /** A clip in this lane has no file behind it. */
  missing: boolean;
  onRelink: (sourceId: string) => void;
}) {
  const clips = clipsInTrack(project, kind, track);
  const first = clips[0];
  const source = first ? sourceById(project, first.sourceId) : undefined;
  const muted = clips.length > 0 && clips.every((clip) => clip.muted);
  const volume = first?.volume ?? 1;
  const label = `${kind === "video" ? "V" : kind === "audio" ? "A" : "T"}${track + 1}`;
  // A lane of photographs gets the picture icon; one with any footage in it, the camera.
  const stills = kind === "video" && clips.every((clip) => sourceById(project, clip.sourceId)?.still);

  /**
   * An empty lane is an invitation, not an instrument.
   *
   * Showing the full head with a fader, a mute and a delete for a lane that
   * holds nothing asserts a file that nobody has chosen — and every control on
   * it is disabled, which is a row of dead buttons pretending to be a mixer.
   * A lane with nothing in it offers the one thing that makes sense there.
   */
  /**
   * On an empty project the two waiting lanes are just labelled: the add
   * buttons are beside the timeline's title, and a row of them here as well
   * was three ways of saying the same thing. Once there is an edit, an empty
   * lane offers to be filled, since then it means "put something *here*".
   */
  if (clips.length === 0 && project.clips.length === 0) {
    return (
      <div
        className="flex w-full items-center gap-2 rounded-[8px] border border-dashed border-rule bg-cream-2/25 px-2 text-muted"
        style={{ height: LANE_HEIGHT }}
      >
        <span className="opacity-60" aria-hidden="true">
          {kind === "audio" ? <AudioIcon /> : kind === "text" ? <TextIcon /> : <VideoIcon />}
        </span>
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="font-mono text-[9px] uppercase tracking-[0.08em] opacity-70">{label}</span>
          <span className="truncate text-[11px]">{kind === "audio" ? "Sound" : kind === "text" ? "Text" : "Picture"}</span>
        </span>
      </div>
    );
  }

  if (clips.length === 0) {
    return (
      <button
        type="button"
        onClick={() =>
          kind === "text"
            ? onAddText(track)
            : void pickMedia({ multiple: true }).then((items) => items.length > 0 && onAddFiles(items, { kind, track }))
        }
        className="flex w-full cursor-pointer items-center gap-2 rounded-[8px] border border-dashed border-rule bg-cream-2/25 px-2 text-left text-muted transition-colors hover:border-pine hover:bg-cream-2/50 hover:text-pine"
        style={{ height: LANE_HEIGHT }}
      >
        <span className="flex size-6 shrink-0 items-center justify-center rounded-[6px] border border-dashed border-current">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
            <path d="M12 5v14" />
            <path d="M5 12h14" />
          </svg>
        </span>
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="font-mono text-[9px] uppercase tracking-[0.08em] opacity-70">{label}</span>
          <span className="truncate text-[11px]">{kind === "text" ? "Add text" : "Add media"}</span>
        </span>
      </button>
    );
  }

  return (
    <div
      className="flex flex-col justify-center gap-1 rounded-[8px] border border-rule/70 bg-surface px-2 py-1"
      style={{ height: LANE_HEIGHT }}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        <span className={kind === "audio" ? "text-teal-deep" : kind === "text" ? "text-wood" : "text-pine"} aria-hidden="true">
          {kind === "audio" ? <AudioIcon /> : kind === "text" ? <TextIcon /> : stills ? <ImageIcon /> : <VideoIcon />}
        </span>
        <span className="font-mono text-[9px] uppercase tracking-[0.08em] text-muted">{label}</span>
        {missing && first ? (
          // The lane is laid out from the record; the file behind it is not here.
          <button
            type="button"
            onClick={() => onRelink(first.sourceId)}
            title={`${source?.name ?? "This file"} isn't loaded. Choose it to fill the lane in.`}
            className="flex min-w-0 flex-1 items-center gap-1 rounded-[5px] border border-warn/50 bg-warn/10 px-1.5 py-px text-left text-[10.5px] text-ink transition-colors hover:border-warn hover:bg-warn/20"
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <path d="M12 9v4M12 17h.01M10.3 3.9 2.6 17.2A2 2 0 0 0 4.3 20h15.4a2 2 0 0 0 1.7-2.8L13.7 3.9a2 2 0 0 0-3.4 0z" />
            </svg>
            <span className="truncate">File missing</span>
            <span className="ml-auto shrink-0 font-medium text-warn">Choose</span>
          </button>
        ) : (
          <span className="min-w-0 flex-1 truncate text-[11px] text-ink" title={first ? clipLabel(first, source) : undefined}>
            {first ? clipLabel(first, source) : null}
          </span>
        )}
      </div>

      <div className="flex items-center gap-1">
        {kind === "audio" ? (
          <>
            <IconButton
              label={muted ? `Unmute ${label}` : `Mute ${label}`}
              onClick={() => onChange(setTrackMuted(project, kind, track, !muted))}
            >
              {muted ? <MutedIcon /> : <SpeakerIcon />}
            </IconButton>
            <input
              type="range"
              className="fader h-1 min-w-0 flex-1"
              min={0}
              max={2}
              step={0.05}
              value={volume}
              disabled={muted}
              aria-label={`${label} volume`}
              onChange={(event) =>
                onChange(setTrackVolume(project, kind, track, Number(event.target.value)))
              }
            />
          </>
        ) : (
          <span className="flex-1 font-mono text-[9px] text-muted">
            {clips.length} clip{clips.length === 1 ? "" : "s"}
          </span>
        )}
        <IconButton
          label={`Delete ${label}`}
          onClick={() => onChange(removeTrack(project, kind, track))}
        >
          <TrashIcon />
        </IconButton>
      </div>
    </div>
  );
}

const VideoIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="6" width="13" height="12" rx="2" />
    <path d="m15 11 6-3.5v9L15 13z" />
  </svg>
);

const AudioIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 13v-2M8 16V8M12 19V5M16 16V8M20 13v-2" />
  </svg>
);

const SpeakerIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 5 6 9H3v6h3l5 4z" />
    <path d="M16 9.5a3.5 3.5 0 0 1 0 5" />
  </svg>
);

const MutedIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 5 6 9H3v6h3l5 4z" />
    <path d="m16 10 5 5M21 10l-5 5" />
  </svg>
);

const TrashIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />
  </svg>
);

/**
 * How far into each end of the view counts as "at the edge", as a fraction of
 * the visible width, with pixel bounds so it is sane on a phone and on a wide
 * monitor.
 *
 * A fixed pixel band felt different in each direction, because how much of the
 * gesture it represents depends on the width it sits in. A proportion is the
 * same distance into the drag whichever way you are going.
 */
const EDGE_FRACTION = 0.2;
const EDGE_MIN = 48;
const EDGE_MAX = 220;
/** Pixels per second of scroll at the very edge, the same in both directions. */
const EDGE_SPEED = 900;
/** Movement under this many pixels is a tap, not a drag. */
const TAP_SLOP = 4;

function ClipBlock({
  project,
  clip,
  pps,
  selected,
  missing,
  scroller,
  onSelect,
  onChange,
}: {
  project: Project;
  clip: Clip;
  pps: number;
  selected: boolean;
  missing: boolean;
  scroller: React.RefObject<HTMLDivElement | null>;
  onSelect: (id: string | null) => void;
  onChange: (project: Project) => void;
}) {
  const source = sourceById(project, clip.sourceId);

  /**
   * A drag has to survive the view moving underneath it.
   *
   * The offset is measured from where the pointer started, so scrolling the
   * container while the pointer sits still would leave the clip behind: its
   * clientX has not changed even though the timeline has. So the scrolling has
   * to be added in — but only the scrolling *this drag performed*, accumulated
   * in `scrolled` below, never the container's current scrollLeft.
   *
   * That distinction is the whole fix for a bug that kept coming back.
   * Shortening an edit shortens the timeline element, and the browser then
   * clamps scrollLeft to fit. Reading scrollLeft meant that clamp counted as
   * leftward movement, which shortened the edit further, which clamped again:
   * a clip that shot to the start whenever it neared the left edge. Counting
   * only our own scrolling makes a clamp invisible to the drag, whatever
   * causes it.
   */
  const drag = useRef<{
    mode: "move" | "in" | "out";
    clientX: number;
    start: number;
    in: number;
    out: number;
    /** Whether the clip was already selected when the press began. */
    wasSelected: boolean;
    /** Set once the pointer has travelled further than a tap. */
    moved: boolean;
  } | null>(null);
  /** A touch on a clip that is not selected: remembered so a tap can select it, never dragged. */
  const tap = useRef<{ clientX: number; clientY: number } | null>(null);
  /** Pixels this drag has scrolled the view by, as actually applied. */
  const scrolled = useRef(0);
  // The autoscroll loop needs the newest pointer position and the newest
  // project, neither of which a closure captured at pointerdown would have.
  const pointerX = useRef(0);
  const apply = useRef<(clientX: number) => void>(() => undefined);
  const frame = useRef(0);

  apply.current = (clientX: number) => {
    const state = drag.current;
    if (!state) return;
    const delta = (clientX - state.clientX + scrolled.current) / pps;
    if (state.mode === "move") onChange(moveClip(project, clip.id, state.start + delta));
    else if (state.mode === "in") onChange(trimClip(project, clip.id, "in", state.in + delta));
    else onChange(trimClip(project, clip.id, "out", state.out + delta));
  };

  const stopScrolling = () => {
    if (frame.current) cancelAnimationFrame(frame.current);
    frame.current = 0;
  };

  /**
   * Scroll while the pointer is held near an edge, at a rate that grows as it
   * gets closer, so a small nudge creeps and the far edge moves quickly.
   */
  const edgeScroll = () => {
    const box = scroller.current;
    if (!box || !drag.current) return stopScrolling();
    const rect = box.getBoundingClientRect();
    const zone = Math.max(EDGE_MIN, Math.min(EDGE_MAX, rect.width * EDGE_FRACTION));
    const fromLeft = pointerX.current - rect.left;
    const fromRight = rect.right - pointerX.current;

    let direction = 0;
    let depth = 0;
    if (fromLeft < zone) {
      direction = -1;
      depth = (zone - fromLeft) / zone;
    } else if (fromRight < zone) {
      direction = 1;
      depth = (zone - fromRight) / zone;
    }
    if (direction === 0) return stopScrolling();

    // Clamped to [0, 1] because the pointer can be dragged clean outside the
    // container, where the raw depth keeps growing and the scroll would
    // accelerate away.
    depth = Math.max(0, Math.min(1, depth));
    const before = box.scrollLeft;
    box.scrollLeft = before + direction * depth * (EDGE_SPEED / 60);
    // What the container actually did, which is nothing once it reaches an end.
    // Accumulating the real movement rather than the amount asked for keeps the
    // clip still when there is nowhere left to scroll.
    const moved = box.scrollLeft - before;
    if (moved !== 0) {
      scrolled.current += moved;
      apply.current(pointerX.current);
    }
    frame.current = requestAnimationFrame(edgeScroll);
  };

  /**
   * One rule for fingers, one for mice, and the same clip underneath.
   *
   * A mouse drags straight away, as it always has: there is nothing else a
   * mouse press on a clip could mean. A finger is different, because the same
   * finger also scrolls the timeline, and the browser decides which it was
   * doing after the first few pixels — by which time a drag that began on
   * pointerdown has already moved the clip. So a finger on a clip that is not
   * selected does nothing but wait for the tap that selects it; the browser
   * keeps the pan. Once a clip is selected, `touch-action: none` on it hands
   * every gesture to us, and dragging moves it or trims it like a mouse would.
   * A tap on the selected clip lets it go again, so with nothing selected a
   * finger anywhere on the lanes scrolls.
   */
  const begin = (mode: "move" | "in" | "out") => (event: React.PointerEvent) => {
    event.stopPropagation();
    if (event.pointerType !== "mouse" && !selected) {
      tap.current = { clientX: event.clientX, clientY: event.clientY };
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    if (!selected) onSelect(clip.id);
    pointerX.current = event.clientX;
    scrolled.current = 0;
    drag.current = {
      mode,
      clientX: event.clientX,
      start: clip.start,
      in: clip.in,
      out: clip.out,
      wasSelected: selected,
      moved: false,
    };
  };

  const move = (event: React.PointerEvent) => {
    const state = drag.current;
    if (!state) return;
    if (!state.moved && Math.abs(event.clientX - state.clientX) < TAP_SLOP) return;
    state.moved = true;
    pointerX.current = event.clientX;
    apply.current(event.clientX);
    if (!frame.current) frame.current = requestAnimationFrame(edgeScroll);
  };

  const end = (event: React.PointerEvent) => {
    // The tap that selects a clip under a finger: down and up without a scroll
    // in between. A scroll cancels the pointer instead, and this never runs.
    if (tap.current) {
      const still =
        Math.abs(event.clientX - tap.current.clientX) < TAP_SLOP &&
        Math.abs(event.clientY - tap.current.clientY) < TAP_SLOP;
      tap.current = null;
      if (still && event.type === "pointerup") onSelect(clip.id);
      return;
    }
    const state = drag.current;
    if (!state) return;
    drag.current = null;
    stopScrolling();
    // A press that went nowhere on a clip that was already selected is a tap
    // on it, and tapping the selected thing again puts it down.
    if (!state.moved && state.wasSelected && state.mode === "move" && event.type === "pointerup") {
      onSelect(null);
    }
  };
  const cancel = () => {
    tap.current = null;
    if (!drag.current) return;
    drag.current = null;
    stopScrolling();
  };

  useEffect(() => stopScrolling, []);

  return (
    <button
      type="button"
      className="clip text-left"
      data-kind={clip.kind}
      data-still={source?.still ? "true" : undefined}
      data-selected={selected}
      data-missing={missing}
      style={{ left: clip.start * pps, width: Math.max(clipDuration(clip) * pps, 8) }}
      onPointerDown={begin("move")}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={cancel}
      onLostPointerCapture={cancel}
      title={clipLabel(clip, source)}
    >
      <span className="pointer-events-none block truncate px-2 py-1 text-[11px] font-medium">
        {clip.kind === "audio" ? "♪ " : clip.kind === "text" ? "T · " : ""}
        {clipLabel(clip, source)}
      </span>
      {selected ? (
        <>
          <span
            className="trim trim-in"
            onPointerDown={begin("in")}
            onPointerMove={move}
            onPointerUp={end}
            onPointerCancel={cancel}
            onLostPointerCapture={cancel}
          />
          <span
            className="trim trim-out"
            onPointerDown={begin("out")}
            onPointerMove={move}
            onPointerUp={end}
            onPointerCancel={cancel}
            onLostPointerCapture={cancel}
          />
        </>
      ) : null}
    </button>
  );
}

/* ---- inspector ----------------------------------------------------------- */

export type Denoising = { clipId: string; fraction: number; note: string } | null;

function MagicIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2.5l1.8 5.2 5.2 1.8-5.2 1.8L12 16.5l-1.8-5.2L5 9.5l5.2-1.8z" />
      <path d="M5 15l.9 2.6 2.6.9-2.6.9L5 22l-.9-2.6L1.5 18.5l2.6-.9z" />
      <path d="M19 14l.7 1.8 1.8.7-1.8.7L19 19l-.7-1.8-1.8-.7 1.8-.7z" />
    </svg>
  );
}

/**
 * Noise removal for an audio clip: one wide button, then while it runs the
 * same button filling up, then a quiet line saying it is done with the way
 * back. It is a row of its own because it changes the sound of the whole
 * clip, which the buttons beside it do not.
 */
function NoiseRow({
  clip,
  state,
  loaded,
  progress,
  busyElsewhere,
  onDenoise,
  onRestore,
}: {
  clip: Clip;
  /** raw: untouched. cleaned: this is the muted original with a copy below. copy: this is the clean copy. */
  state: "raw" | "cleaned" | "copy";
  loaded: boolean;
  progress: NonNullable<Denoising> | null;
  busyElsewhere: boolean;
  onDenoise: () => void;
  onRestore: () => void;
}) {
  if (state !== "raw") {
    return (
      <div className="flex items-start justify-between gap-3 rounded-[10px] border border-pine/30 bg-pine/[0.06] px-3 py-2">
        <span className="flex min-w-0 items-start gap-2 text-[12px] leading-snug text-pine">
          <span className="mt-px shrink-0"><MagicIcon /></span>
          <span>
            <span className="font-medium">Noise removed.</span>{" "}
            <span className="text-soft">
              {state === "copy" ? "This is the clean copy." : "The clean copy is on the track below; this one is muted."}
            </span>
          </span>
        </span>
        <button type="button" onClick={onRestore} className="link shrink-0 text-[12px] text-soft hover:text-pine">
          Undo
        </button>
      </div>
    );
  }
  if (progress) {
    return (
      <button type="button" disabled className="magic" aria-live="polite">
        <span className="magic-fill" style={{ transform: `scaleX(${progress.fraction})` }} />
        <MagicIcon />
        <span>
          {progress.note}
          <span className="ml-2 font-mono text-[11px] font-normal opacity-80">
            {Math.round(progress.fraction * 100)}%
          </span>
        </span>
      </button>
    );
  }
  return (
    <button
      type="button"
      className="magic"
      disabled={!loaded || busyElsewhere}
      onClick={onDenoise}
      title={
        !loaded
          ? "Load this clip's file first"
          : `Take the hiss and room tone out of ${clip.kind === "audio" ? "this clip" : "the sound"}`
      }
    >
      <MagicIcon />
      Remove noise
    </button>
  );
}

/**
 * The one control in the inspector that depends on where the playhead is, so
 * the one thing in the inspector that re-renders as it moves.
 */
function SplitButton({
  project,
  clip,
  playhead,
  onChange,
}: {
  project: Project;
  clip: Clip;
  playhead: PlayheadStore;
  onChange: (project: Project) => void;
}) {
  // Only whether a split is possible, which flips at the clip's edges rather
  // than with every step of the playhead.
  const inside = usePlayheadDerived(playhead, (at) => at > clip.start && at < clipEnd(clip));
  return (
    <button
      type="button"
      className="pill"
      disabled={!inside}
      onClick={() => onChange(splitClip(project, clip.id, playhead.now))}
    >
      Split at playhead
    </button>
  );
}

export function Inspector({
  project,
  clip,
  freshText,
  playhead,
  resolved,
  caps,
  denoising,
  onDenoise,
  onRestore,
  onChange,
  onSelect,
  onRelink,
  onRelinkMany,
  onReopen,
  canReopen,
}: {
  project: Project;
  clip: Clip | null;
  /** The title just made, whose words should take the caret. */
  freshText: string | null;
  playhead: PlayheadStore;
  resolved: Map<string, ResolvedSource>;
  caps: Capabilities;
  denoising: Denoising;
  onDenoise: (clip: Clip) => void;
  onRestore: (clip: Clip) => void;
  onChange: (project: Project) => void;
  onSelect: (id: string | null) => void;
  /** Supply one source's file. */
  onRelink: (sourceId: string) => void;
  /** Supply several at once, matched to whatever is missing. */
  onRelinkMany: () => void;
  /** Reopen from stored handles, with permission. Shown only when `canReopen`. */
  onReopen: () => void;
  canReopen: boolean;
}) {
  const source = clip ? sourceById(project, clip.sourceId) : null;
  const missing = project.sources.filter((entry) => !entry.derived && !resolved.has(entry.id));

  return (
    // Beside the editor, the panel is exactly as tall as the editor: zero height
    // of its own so it adds nothing to the row, then stretched to the row. The
    // clip card takes what is left and scrolls inside it rather than pushing
    // the page down. On a narrow screen the cards stack and this does not apply.
    <aside className="flex flex-col gap-3 lg:h-0 lg:min-h-full">
      <section className="shrink-0 rounded-[12px] border border-rule bg-surface px-4 py-3">
        <p className="eyebrow">Composition</p>
        {isAudioOnly(project) ? (
          <p className="mt-1.5 font-mono text-[11px] text-soft">Sound only · exports as M4A</p>
        ) : (
          <CompositionPanel project={project} caps={caps} onChange={onChange} />
        )}
      </section>

      {missing.length > 0 ? (
        <section className="rounded-[12px] border border-warn/40 bg-warn/10 p-4">
          <p className="eyebrow">Files not loaded</p>
          <p className="mt-2 text-[13px] leading-[1.5] text-ink">
            The edit is intact. These clips show a placeholder until you point at their files again.
          </p>
          <ul className="mt-3 flex flex-col gap-1.5">
            {missing.map((entry) => (
              <li key={entry.id} className="flex items-center gap-2 font-mono text-[11px] text-soft">
                <span className="min-w-0 flex-1 truncate" title={entry.name}>
                  {entry.name} · {formatBytes(entry.size)}
                </span>
                <button type="button" onClick={() => onRelink(entry.id)} className="link shrink-0 text-ink hover:text-pine">
                  Choose
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex flex-wrap gap-2">
            {canReopen ? (
              <button type="button" onClick={onReopen} className="pill-solid">
                Open the files
              </button>
            ) : null}
            <button type="button" onClick={onRelinkMany} className="pill">
              Choose files…
            </button>
          </div>
        </section>
      ) : null}

      <section className="scroll-thin min-h-0 overflow-y-auto rounded-[12px] border border-rule bg-surface p-4">
        <p className="eyebrow">Clip</p>
        {!clip || (clip.kind !== "text" && !source) ? (
          <p className="mt-2 font-serif text-[15px] italic text-muted">
            Select a clip on the timeline to trim it or change its level.
          </p>
        ) : (
          <div className="mt-3 flex flex-col gap-3.5">
            <div>
              <p className="truncate font-serif text-[16px] leading-tight text-pine" title={clipLabel(clip, source ?? undefined)}>
                {clipLabel(clip, source ?? undefined)}
              </p>
              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-muted">
                {source?.still ? "image" : clip.kind} · track {clip.track + 1} · {timecode(clipDuration(clip))}
              </p>
            </div>

            {clip.kind === "text" ? (
              <TextPanel project={project} clip={clip} focus={freshText === clip.id} onChange={onChange} />
            ) : null}

            {clip.kind === "audio" ? (
              <label className="flex flex-col gap-1.5">
                <span className="flex items-baseline justify-between text-[12px] text-soft">
                  Volume
                  <span className="font-mono text-[11px] text-muted">
                    {clip.muted ? "muted" : `${Math.round(clip.volume * 100)}%`}
                  </span>
                </span>
                <input
                  type="range"
                  className="fader"
                  min={0}
                  max={2}
                  step={0.05}
                  value={clip.volume}
                  disabled={clip.muted}
                  onChange={(event) =>
                    onChange(updateClip(project, clip.id, { volume: Number(event.target.value) }))
                  }
                />
              </label>
            ) : clip.kind === "video" ? (
              // A video clip has no level of its own: its sound was split onto
              // its own track at import, and that is where the fader lives.
              <Sound project={project} clip={clip} onSelect={onSelect} />
            ) : null}

            {clip.kind === "audio" ? (
              <NoiseRow
                clip={clip}
                state={clip.cleanOf ? "copy" : cleanCopyOf(project, clip) ? "cleaned" : "raw"}
                loaded={resolved.has(clip.sourceId)}
                progress={denoising?.clipId === clip.id ? denoising : null}
                busyElsewhere={Boolean(denoising) && denoising?.clipId !== clip.id}
                onDenoise={() => onDenoise(clip)}
                onRestore={() => onRestore(clip)}
              />
            ) : null}

            {isVisual(clip.kind) ? (
              <>
                <Layout project={project} clip={clip} source={source ?? undefined} onChange={onChange} />
                <AnimationPanel project={project} clip={clip} onChange={onChange} />
              </>
            ) : null}

            <div className="flex flex-wrap gap-2">
              {clip.kind === "audio" ? (
                <button
                  type="button"
                  className="pill"
                  onClick={() => onChange(updateClip(project, clip.id, { muted: !clip.muted }))}
                >
                  {clip.muted ? "Unmute" : "Mute"}
                </button>
              ) : null}
              <SplitButton project={project} clip={clip} playhead={playhead} onChange={onChange} />
              <button
                type="button"
                className="pill"
                onClick={() => {
                  onChange(removeClip(project, clip.id));
                  onSelect(null);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        )}
      </section>
    </aside>
  );
}

/* ---- layout -------------------------------------------------------------- */

/**
 * Where a video clip's sound went, and a way to get to it.
 *
 * Shown in place of a fader, because a video clip does not have one: the file's
 * audio became its own clip on its own track at import, and levelling it there
 * is the only thing that has any effect.
 */
export function Sound({
  project,
  clip,
  onSelect,
}: {
  project: Project;
  clip: Clip;
  onSelect: (id: string | null) => void;
}) {
  const sound = soundFor(project, clip);
  // A file with no sound needs no explanation: there is simply nothing to say
  // about a level that does not exist.
  if (!sound) return null;
  return (
    <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[12px] leading-[1.45] text-muted">
      <span>
        Its sound is on <span className="font-mono text-[11px] text-soft">A{sound.track + 1}</span>.
      </span>
      <button type="button" className="link font-medium text-pine" onClick={() => onSelect(sound.id)}>
        Select that track
      </button>
    </p>
  );
}

/**
 * The five positions a clip can be dropped into, laid out where they put it.
 *
 * The grid is the point: the button in the top-left cell puts the clip in the
 * top-left corner, so nothing has to be read and translated into a place.
 */
const CORNER_PRESETS: { name: PresetName; label: string; cell: string }[] = [
  { name: "top-left", label: "Top left", cell: "col-start-1 row-start-1" },
  { name: "top-right", label: "Top right", cell: "col-start-3 row-start-1" },
  { name: "centre", label: "Centre", cell: "col-start-2 row-start-2" },
  { name: "bottom-left", label: "Bottom left", cell: "col-start-1 row-start-3" },
  { name: "bottom-right", label: "Bottom right", cell: "col-start-3 row-start-3" },
];

/** A frame with the target area filled, so the button looks like what it does. */
function PresetIcon({ name, centre = false }: { name: PresetName; centre?: boolean }) {
  const inset =
    name === "centre"
      ? { x: 8.5, y: 6.5 }
      : {
          x: name === "top-left" || name === "bottom-left" ? 3.5 : 13.5,
          y: name === "top-left" || name === "top-right" ? 3 : 10,
        };
  const width = centre ? 34 : 22;
  return (
    <svg width={width} height={Math.round((width / 26) * 18)} viewBox="0 0 26 18" aria-hidden="true">
      <rect x="0.8" y="0.8" width="24.4" height="16.4" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.3" opacity="0.45" />
      <rect x={inset.x} y={inset.y} width="9" height="5" rx="1.2" fill="currentColor" />
    </svg>
  );
}

const RestoreIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1" />
    <path d="M3 4v5h5" />
  </svg>
);

/**
 * The preset grid.
 *
 * Each button fills its cell rather than shrinking to its icon: they were
 * small, evenly spaced islands before, which made a set of five choices read
 * as a scattering. The one the clip is already on is filled, so the control
 * reports the current position as well as offering to change it.
 */
function PositionPicker({
  project,
  clip,
  source,
  onChange,
}: {
  project: Project;
  clip: Clip;
  source: SourceRef | undefined;
  onChange: (project: Project) => void;
}) {
  const current = matchingPreset(project, clip, source);
  return (
    <div
      role="group"
      aria-label="Position in the frame"
      // A wider middle column and rows that size to their contents: the centre
      // is the common choice and reads as the primary one, and equal rows had
      // been keeping the corner buttons as tall as it.
      className="grid grid-cols-[1fr_2fr_1fr] items-center gap-1"
    >
      {CORNER_PRESETS.map((preset) => {
        const active = current === preset.name;
        return (
          <button
            key={preset.name}
            type="button"
            title={preset.label}
            aria-label={preset.label}
            aria-pressed={active}
            onClick={() => onChange(applyPreset(project, clip.id, preset.name))}
            className={`${preset.cell} flex items-center justify-center rounded-[7px] border transition-colors ${
              preset.name === "centre" ? "py-3" : "py-1"
            } ${
              active
                ? "border-pine-deep bg-pine text-cream"
                : "border-rule bg-cream-2/40 text-pine hover:border-pine hover:bg-cream-2"
            }`}
          >
            <PresetIcon name={preset.name} centre={preset.name === "centre"} />
          </button>
        );
      })}
    </div>
  );
}

/**
 * Where a video clip sits in the frame.
 *
 * The presets carry this panel: a reaction cam goes in one of four corners
 * roughly always, and dragging one there by hand is a worse way to get the
 * same result. Everything below them is for the cases the presets miss.
 */
const FIT_OPTIONS: { mode: Clip["transform"]["fit"]; label: string; hint: string }[] = [
  { mode: "cover", label: "Cover", hint: "Fill the box and crop whatever hangs over the edges" },
  { mode: "contain", label: "Contain", hint: "Show the whole picture, leaving gaps in the box" },
  { mode: "stretch", label: "Stretch", hint: "Fill the box exactly, distorting the picture" },
];

/**
 * Where a video clip sits in the frame and what shape it is.
 *
 * Grouped rather than listed. There are a dozen controls here and, flat, they
 * read as a heap: the ones that move a clip, the ones that size it and the ones
 * that change how it looks all want different moments, so each group is
 * labelled and separated and the fit choice gets a row to itself.
 */
export function Layout({
  project,
  clip,
  source,
  onChange,
}: {
  project: Project;
  clip: Clip;
  /** Absent for a title, which has no file and whose height is its words. */
  source: SourceRef | undefined;
  onChange: (project: Project) => void;
}) {
  const text = clip.kind === "text";
  /**
   * The sizing mode is read back from the geometry, so it can never disagree
   * with the box. One consequence needed softening: a box that happens to be
   * the whole frame *is* the filled state, so asking for "Free" on one had no
   * visible effect and looked like the click had been ignored.
   *
   * This override is presentational only. The geometry is identical either
   * way; all it decides is which button is lit and whether the width and
   * height fields are offered, and it lapses as soon as the geometry stops
   * being ambiguous.
   */
  const derived = sizeMode(clip.transform);
  const [preferFree, setPreferFree] = useState(false);
  useEffect(() => {
    if (derived === "locked") setPreferFree(false);
  }, [derived]);
  const mode = preferFree && derived === "filled" ? "free" : derived;
  const locked = mode === "locked";
  const set = (patch: Parameters<typeof clampTransform>[0]) =>
    onChange(setTransform(project, clip.id, clampTransform(patch)));

  // The model anchors a clip by its middle, because that is what sizing and
  // rotation turn around and what is clamped to stay reachable. The panel
  // reports the top-left corner instead, which is what people mean by a
  // position, so the two are converted here rather than anywhere deeper.
  const boxW = clip.transform.scale;
  const boxH = clipBoxHeight(project, clip, source);
  const left = clip.transform.x - boxW / 2;
  const top = clip.transform.y - boxH / 2;

  return (
    <div className="flex flex-col gap-3.5 border-t border-rule pt-3.5">
      <Group label="Position" first>
        <PositionPicker project={project} clip={clip} source={source} onChange={onChange} />
        <MeasureField
          label="Position X"
          fraction={left}
          pixels={project.width}
          min={-boxW / 2}
          max={1 - boxW / 2}
          onChange={(value) => set({ x: value + boxW / 2 })}
        />
        <MeasureField
          label="Position Y"
          fraction={top}
          pixels={project.height}
          min={-boxH / 2}
          max={1 - boxH / 2}
          onChange={(value) => set({ y: value + boxH / 2 })}
        />
      </Group>

      {text ? (
        <Group label="Size">
          {/* A title's height is either its words' or a number: two states, and
              the "filled" one a picture has is meaningless for it. */}
          <Segmented<"locked" | "free">
            label="Height"
            options={[
              { value: "locked", label: "Follows the words", hint: "As tall as the text comes out" },
              { value: "free", label: "Fixed", hint: "Set the height yourself; the words sit inside" },
            ]}
            selected={locked ? "locked" : "free"}
            onSelect={(next) => onChange(setAspectLocked(project, clip.id, next === "locked"))}
          />
          <MeasureField
            label="Width"
            fraction={boxW}
            pixels={project.width}
            min={0.05}
            max={1}
            onChange={(scale) => set({ scale, x: left + scale / 2 })}
          />
          {locked ? null : (
            <MeasureField
              label="Height"
              fraction={boxH}
              pixels={project.height}
              min={0.02}
              max={1}
              onChange={(height) => set({ height, y: top + height / 2 })}
            />
          )}
          <button
            type="button"
            onClick={() => onChange(fitTextBox(project, clip.id))}
            className="flex items-center justify-center gap-1.5 rounded-[6px] border border-teal-deep/40 bg-teal/10 py-1 text-[10px] font-medium uppercase tracking-[0.06em] text-teal-deep transition-colors hover:border-teal-deep hover:bg-teal/20"
          >
            <RestoreIcon />
            Shrink the box to the words
          </button>
        </Group>
      ) : null}

      {text ? null : (
      <Group label="Size">
        {/* Three exclusive states, three buttons. Filling the frame used to be a
            separate action that silently changed the lock, so the toggle could
            end up describing something other than what had just happened. */}
        <Segmented
          label="Sizing"
          options={[
            { value: "filled" as const, label: "Filled", hint: "The box is the whole frame" },
            { value: "locked" as const, label: "Locked", hint: "Keep the original proportions" },
            { value: "free" as const, label: "Free", hint: "Width and height move independently" },
          ]}
          selected={mode}
          onSelect={(next) => {
            setPreferFree(next === "free");
            onChange(setSizeMode(project, clip.id, next));
          }}
        />

        {mode !== "locked" && source && !boxMatchesSource(project, clip.transform, source) ? (
          <button
            type="button"
            onClick={() => onChange(resetAspect(project, clip.id))}
            className="flex items-center justify-center gap-1.5 rounded-[6px] border border-teal-deep/40 bg-teal/10 py-1 text-[10px] font-medium uppercase tracking-[0.06em] text-teal-deep transition-colors hover:border-teal-deep hover:bg-teal/20"
          >
            <RestoreIcon />
            Restore original ratio
          </button>
        ) : null}

        {/* Filled has no size to set: the box is the frame. Touching anything
            that would stop that being true moves the mode on by itself, because
            the mode is read back from the geometry rather than remembered. */}
        {mode === "filled" ? null : locked ? (
          <MeasureField
            label="Scale"
            fraction={boxW}
            pixels={project.width}
            min={0.05}
            max={2}
            onChange={(scale) => set({ scale, x: left + scale / 2 })}
          />
        ) : (
          <>
            <MeasureField
              label="Width"
              fraction={boxW}
              pixels={project.width}
              min={0.05}
              max={2}
              // The panel reports the top-left corner, so growing a clip has to
              // extend it rightwards rather than outwards from its middle.
              // Dragging a corner on the canvas still works about the centre,
              // where the movement itself shows what is happening.
              onChange={(scale) => set({ scale, x: left + scale / 2 })}
            />
            <MeasureField
              label="Height"
              fraction={boxH}
              pixels={project.height}
              min={0.05}
              max={2}
              onChange={(height) => set({ height, y: top + height / 2 })}
            />
          </>
        )}
      </Group>
      )}

      {/* Shown only when the box is a different shape from the picture, which is
          the only time any of the three modes draws anything different. */}
      {source && fitMatters(project, clip.transform, source) ? (
        <Group label="Fit">
          <Segmented
            label="How the picture fills the box"
            options={FIT_OPTIONS.map((option) => ({
              value: option.mode,
              label: option.label,
              hint: option.hint,
            }))}
            selected={clip.transform.fit}
            onSelect={(fit) => set({ fit })}
          />
        </Group>
      ) : null}

      <Group label="Appearance">
        {/* A title's rounding belongs to its background, and is set with it. */}
        {text ? null : (
          <Slider
            label="Corner rounding"
            value={clip.transform.radius}
            min={0}
            max={0.5}
            step={0.01}
            format={(value) => (value >= 0.5 ? "circle" : `${Math.round(value * 200)}%`)}
            onChange={(radius) => set({ radius })}
          />
        )}
        <Slider
          label="Opacity"
          value={clip.transform.opacity}
          min={0}
          max={1}
          step={0.01}
          format={(value) => `${Math.round(value * 100)}%`}
          onChange={(opacity) => set({ opacity })}
        />
        <Slider
          label="Rotation"
          value={clip.transform.rotation > 180 ? clip.transform.rotation - 360 : clip.transform.rotation}
          min={-180}
          max={180}
          step={1}
          format={(value) => `${Math.round(value)}°`}
          onChange={(rotation) => set({ rotation })}
          action={
            <button
              type="button"
              className="font-mono text-[10px] text-pine underline underline-offset-2"
              onClick={() => onChange(rotateClip(project, clip.id))}
            >
              +90°
            </button>
          }
        />
      </Group>

      <Group label="Stacking">
        <div className="flex overflow-hidden rounded-[8px] border border-rule">
          <StackButton
            label="Bring forward"
            disabled={!canReorder(project, clip.id, 1)}
            onClick={() => onChange(reorderClip(project, clip.id, 1))}
          >
            <ForwardIcon />
          </StackButton>
          <StackButton
            label="Send back"
            divided
            disabled={!canReorder(project, clip.id, -1)}
            onClick={() => onChange(reorderClip(project, clip.id, -1))}
          >
            <BackIcon />
          </StackButton>
        </div>
      </Group>

    </div>
  );
}

function StackButton({
  label,
  disabled,
  divided,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  divided?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={`flex flex-1 items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium transition-colors ${
        divided ? "border-l border-rule" : ""
      } ${
        disabled
          ? "cursor-default bg-cream-2/20 text-muted/60"
          : "bg-cream-2/40 text-pine hover:bg-cream-2"
      }`}
    >
      {children}
      {label}
    </button>
  );
}

const ForwardIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="4" width="11" height="11" rx="2" fill="currentColor" stroke="none" opacity="0.9" />
    <path d="M9 20h11V9" />
  </svg>
);

const BackIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="9" y="9" width="11" height="11" rx="2" fill="currentColor" stroke="none" opacity="0.9" />
    <path d="M15 4H4v11" />
  </svg>
);

/* ---- helpers ------------------------------------------------------------- */

export function timecode(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const whole = Math.floor(seconds);
  const minutes = Math.floor(whole / 60);
  const secs = whole % 60;
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}

export type { ReactNode };
